/**
 * Phase 7 / E1 — client-side error reporter.
 *
 * Captures uncaught errors and unhandled promise rejections, POSTs a
 * structured payload to `/errors` (same pattern as `/metrics`). Opt-out
 * via `localStorage.errorReporting = '0'`.
 *
 * Designed to be loud-enough to catch real production breakage without
 * spamming. A simple dedup ring (last 16 message+stack hashes) suppresses
 * floods.
 */

const DEDUP_SIZE = 16

const enabled = (() => {
  try {
    if (typeof localStorage !== 'undefined' && localStorage.errorReporting === '0') return false
  } catch { /* ssr */ }
  return true
})()

const recentHashes: string[] = []

const hash = (s: string): string => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return (h >>> 0).toString(16)
}

const post = (kind: string, payload: Record<string, any>) => {
  try {
    const key = hash(kind + ':' + (payload.message ?? '') + ':' + (payload.stack ?? '').slice(0, 200))
    if (recentHashes.includes(key)) return
    recentHashes.push(key)
    if (recentHashes.length > DEDUP_SIZE) recentHashes.shift()
    const body = JSON.stringify({
      v: 1,
      kind,
      ua: navigator.userAgent.slice(0, 256),
      url: { path: location.pathname, search: location.search },
      ...payload,
    })
    const blob = new Blob([body], { type: 'application/json' })
    if (navigator.sendBeacon) navigator.sendBeacon('/errors', blob)
    else fetch('/errors', { method: 'POST', body: blob, keepalive: true }).catch(() => { /* silent */ })
  } catch { /* never let the reporter throw */ }
}

if (typeof window !== 'undefined' && enabled) {
  window.addEventListener('error', (ev) => {
    if (!ev.error && !ev.message) return
    post('error', {
      message: String(ev.message ?? ev.error?.message ?? '').slice(0, 512),
      stack: String(ev.error?.stack ?? '').slice(0, 2048),
      filename: String(ev.filename ?? '').slice(0, 256),
      lineno: ev.lineno,
      colno: ev.colno,
    })
  })

  window.addEventListener('unhandledrejection', (ev) => {
    const reason: any = (ev as PromiseRejectionEvent).reason
    post('unhandledrejection', {
      message: String(reason?.message ?? reason ?? '').slice(0, 512),
      stack: String(reason?.stack ?? '').slice(0, 2048),
    })
  })
}
